/**
 * CDN Configuration for TMF React Frontend
 *
 * Configuração centralizada para URLs de CDN multi-tenant.
 *
 * ARQUITETURA MULTI-TENANT:
 * - Cada cliente tem CDN dedicado via CloudFront
 * - O CDN URL é obtido dinamicamente do backend via /api/v1/core/dev/storage
 * - Fallback para VITE_CDN_BASE_URL se backend não disponível
 *
 * CloudFront por cliente (clients.json):
 * - apex: d1jirfc4ku392x.cloudfront.net
 * - copa: d2fqccqp8o28ql.cloudfront.net
 * - demo: d1542znrqv6bu9.cloudfront.net
 * - drogacenter: d2cw6khqts30s8.cloudfront.net
 * - pearson: d3gws4n8yezmeh.cloudfront.net
 * - s2: d15iymy6wznpma.cloudfront.net
 * - suvinil: d245xjgzjpak7p.cloudfront.net
 *
 * Estrutura da URL CDN:
 * {CDN_BASE_URL}/{CDN_FILES_PATH}/{path}
 *
 * @since 2025-12-17
 * @updated 2026-01-12 - CDN URL dinâmico via cdnStore
 */

import { getCurrentCdnUrl } from '@/store/cdnStore';

/**
 * Default CDN URL (fallback apenas para desenvolvimento local)
 * Em PRODUÇÃO, o CDN é SEMPRE carregado dinamicamente do backend.
 */
function getDefaultCdnUrl(): string {
  // Em produção, NÃO usar fallback hardcoded
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.endsWith('.trademarketingforce.com')) {
      // Produção: CDN DEVE vir do backend, sem fallback
      return '';
    }
  }

  // Desenvolvimento local: permitir fallback do .env
  return import.meta.env.VITE_CDN_BASE_URL || '';
}

const DEFAULT_CDN_URL = getDefaultCdnUrl();

/**
 * Detecta se deve usar CDN direto
 * Em produção (trademarketingforce.com): true
 * Em desenvolvimento: usa VITE_USE_CDN
 */
function detectUseCdn(): boolean {
  // Se configurado explicitamente via env var, usar
  if (import.meta.env.VITE_USE_CDN !== undefined) {
    return import.meta.env.VITE_USE_CDN === 'true';
  }

  // Em runtime, detectar pelo hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Em produção (domínios reais), usar CDN direto
    return hostname.endsWith('.trademarketingforce.com');
  }

  return false;
}

// Configuração do CDN (carregada das variáveis de ambiente)
export const CDN_CONFIG = {
  /** URL base do CDN (fallback - usar getCdnBaseUrl() para URL dinâmico) */
  baseUrl: DEFAULT_CDN_URL,

  /** Caminho base para arquivos */
  filesPath: import.meta.env.VITE_CDN_FILES_PATH || 'files',

  /** Usar CDN direto (true) ou ImageProxy (false) */
  useCdn: detectUseCdn(),
} as const;

/**
 * Verifica se deve usar CDN direto ou ImageProxy
 */
export function shouldUseCdn(): boolean {
  return CDN_CONFIG.useCdn;
}

/**
 * Obtém a URL base do CDN do cliente ativo
 * Usa o cdnStore para obter URL dinâmico por cliente
 */
function getActiveCdnUrl(): string {
  // Tenta obter do store (CDN do cliente ativo)
  try {
    const storeUrl = getCurrentCdnUrl();
    if (storeUrl) {
      return storeUrl;
    }
  } catch {
    // Store não disponível ainda
  }

  // Em produção, se não conseguiu carregar do store, é um erro crítico
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.endsWith('.trademarketingforce.com') && !DEFAULT_CDN_URL) {
      console.error('❌ ERRO: CDN não foi carregado do backend em produção!');
      console.error('Certifique-se que /api/v1/core/dev/storage está acessível');
      // Em produção, não deveria usar fallback
      return '';
    }
  }

  return DEFAULT_CDN_URL;
}

/**
 * Gera URL base do CDN
 *
 * @returns URL base (ex: https://d1jirfc4ku392x.cloudfront.net/files)
 */
export function getCdnBaseUrl(): string {
  const cdnUrl = getActiveCdnUrl();
  return `${cdnUrl}/${CDN_CONFIG.filesPath}`;
}

/**
 * Gera URL completa do CDN para um arquivo
 *
 * @param path - Caminho relativo do arquivo (ex: images/content/contentImage/file.png)
 * @returns URL completa do arquivo no CDN
 *
 * @example
 * getCdnUrl('images/content/contentImage/file.png')
 * // => https://d1jirfc4ku392x.cloudfront.net/files/images/content/contentImage/file.png
 */
export function getCdnUrl(path: string): string {
  // Remove barra inicial se presente
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${getCdnBaseUrl()}/${cleanPath}`;
}

/**
 * Gera URL de imagem de conteúdo
 *
 * @param imagePath - Caminho da imagem do banco (ex: 7e67f7425d.png)
 * @param _size - Parâmetro reservado (CDN não usa thumbnails separados)
 * @returns URL completa da imagem
 *
 * @example
 * getContentImageUrl('7e67f7425d.png')
 * // => https://d1jirfc4ku392x.cloudfront.net/files/images/content/contentImage/7e67f7425d.png
 */
export function getContentImageUrl(imagePath: string, _size: string = '500_500'): string {
  // Se já é uma URL completa, retorna como está
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Remove caminho base se presente
  const filename = imagePath.split('/').pop() || imagePath;

  // Se não usar CDN (desenvolvimento local sem CDN configurado), usar ImageProxy do Python API
  if (!shouldUseCdn()) {
    const fullPath = `content/contentImage/${filename}`;
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(fullPath)}`;
  }

  // Usar CDN - caminho: /files/images/content/contentImage/{filename}
  return getCdnUrl(`images/content/contentImage/${filename}`);
}

/**
 * Gera URL de imagem original (sem redimensionamento)
 *
 * @param imagePath - Caminho da imagem
 * @returns URL da imagem original
 */
export function getContentImageOriginalUrl(imagePath: string): string {
  // Se já é uma URL completa, retorna como está
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const filename = imagePath.split('/').pop() || imagePath;

  // Se não usar CDN, usar ImageProxy do Python API
  if (!shouldUseCdn()) {
    const fullPath = `content/contentImage/${filename}`;
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(fullPath)}`;
  }

  return getCdnUrl(`images/content/contentImage/original/${filename}`);
}

/**
 * Gera URL de imagem de produto
 *
 * @param imagePath - Caminho da imagem
 * @param size - Tamanho do thumbnail
 * @returns URL da imagem de produto
 */
export function getProductImageUrl(imagePath: string, size: string = '500_500'): string {
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const filename = imagePath.split('/').pop() || imagePath;

  // Se não usar CDN, usar ImageProxy do Python API
  if (!shouldUseCdn()) {
    const fullPath = `product/${filename}`;
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(fullPath)}`;
  }

  return getCdnUrl(`images/product/thumb/${size}/${filename}`);
}

/**
 * Gera URL de imagem de pessoa
 *
 * @param imagePath - Caminho da imagem
 * @param size - Tamanho do thumbnail
 * @returns URL da imagem de pessoa
 */
export function getPersonImageUrl(imagePath: string, size: string = '150_150'): string {
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const filename = imagePath.split('/').pop() || imagePath;

  // Se não usar CDN, usar ImageProxy do Python API
  if (!shouldUseCdn()) {
    const fullPath = `person/${filename}`;
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(fullPath)}`;
  }

  return getCdnUrl(`images/person/thumb/${size}/${filename}`);
}

/**
 * Gera URL de imagem de tema (logo, background)
 *
 * @param imagePath - Caminho da imagem ou URL completa
 * @param type - Tipo de imagem: 'logo' ou 'background'
 * @returns URL da imagem de tema
 *
 * @example
 * getThemeImageUrl('logo_demo.png', 'logo')
 * // => https://d1jirfc4ku392x.cloudfront.net/files/images/theme/logos/logo_demo.png
 */
export function getThemeImageUrl(imagePath: string, type: 'logo' | 'background' = 'logo'): string {
  // Se vazio, retorna vazio
  if (!imagePath) {
    return '';
  }

  // Se já é uma URL completa, retorna como está
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const filename = imagePath.split('/').pop() || imagePath;
  const folder = type === 'logo' ? 'logos' : 'backgrounds';

  // Se não usar CDN, usar ImageProxy do Python API
  if (!shouldUseCdn()) {
    const fullPath = `theme/${folder}/${filename}`;
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(fullPath)}`;
  }

  return getCdnUrl(`images/theme/${folder}/${filename}`);
}

export default {
  config: CDN_CONFIG,
  shouldUseCdn,
  getCdnBaseUrl,
  getCdnUrl,
  getContentImageUrl,
  getContentImageOriginalUrl,
  getProductImageUrl,
  getPersonImageUrl,
  getThemeImageUrl,
};
